import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ArrowSquareOut,
  CheckCircle,
  Gear,
  Info,
  Key,
  Plugs,
  ShieldCheck,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useSaveGrowCredentialsMutation } from "../../store/services/growApi";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import GrowSettingsForm from "./GrowSettingsForm";
import {
  GROW_BACKOFFICE_URL,
  GROW_ID_PATTERN,
  buildGrowSettingsState,
  serializeGrowSettings,
} from "./growUi";
import "./GrowIntegration.css";

const TABS = [
  { id: "intro", label: "איך זה עובד", icon: Info },
  { id: "credentials", label: "פרטי חיבור", icon: Key },
  { id: "settings", label: "הגדרות", icon: Gear },
];

/**
 * GrowSetupWizard — manual connect flow ("יש לי חשבון Grow").
 * Clickable tabs (not steps, per design 2026). Save is enabled once the
 * credentials passed "בדיקת חיבור" (dryRun probe on the server).
 *
 * Props: isOpen, onClose, onSuccess?(growIntegration)
 * Error codes: CK-3860 (probe), CK-3861 (save)
 */
export default function GrowSetupWizard({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const [saveGrowCredentials, { isLoading }] = useSaveGrowCredentialsMutation();

  const [tab, setTab] = useState("intro");
  const [userId, setUserId] = useState("");
  const [pageCode, setPageCode] = useState("");
  const [accountPageCode, setAccountPageCode] = useState(""); // optional — SetAccountDetails id (Grow 2026-11-03)
  const [probe, setProbe] = useState({ state: "idle", message: "" }); // idle | checking | ok | error
  const [settings, setSettings] = useState(() => buildGrowSettingsState(clinicProfile, null));
  const [done, setDone] = useState(null); // growIntegration after save
  const [saveError, setSaveError] = useState("");

  // Reset ONLY when the modal opens. `save()` dispatches setClinicProfile, so
  // depending on clinicProfile here would wipe the success screen right after
  // a successful connect.
  useEffect(() => {
    if (!isOpen) return;
    setTab("intro");
    setUserId("");
    setPageCode("");
    setAccountPageCode("");
    setProbe({ state: "idle", message: "" });
    setSettings(buildGrowSettingsState(clinicProfile, null));
    setDone(null);
    setSaveError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const idsValid = GROW_ID_PATTERN.test(userId.trim()) && GROW_ID_PATTERN.test(pageCode.trim());
  const accountCodeValid = !accountPageCode.trim() || GROW_ID_PATTERN.test(accountPageCode.trim());
  const canSave = probe.state === "ok" && idsValid && accountCodeValid && !isLoading;

  const environment = useMemo(
    () => (process.env.REACT_APP_GROW_ENV === "production" ? "production" : "sandbox"),
    []
  );

  const onIdsChange = (setter) => (e) => {
    setter(e.target.value.trim());
    if (probe.state !== "idle") setProbe({ state: "idle", message: "" });
  };

  const runProbe = async () => {
    if (!idsValid) {
      setProbe({ state: "error", message: "מזהה העסק וקוד העמוד חייבים להיות באנגלית/ספרות, 4–64 תווים." });
      return;
    }
    setProbe({ state: "checking", message: "" });
    const res = await saveGrowCredentials({ clinicId, userId, pageCode, environment, source: "manual", dryRun: true });
    if (res.error) {
      console.error("[CK-3860] Grow credentials probe failed", {
        errorCode: "CK-3860",
        errorMessage: res.error.message,
        growErrorId: res.error.details?.growErrorId ?? null,
        timestamp: new Date().toISOString(),
      });
      setProbe({ state: "error", message: res.error.message });
      return;
    }
    setProbe({ state: "ok", message: "החיבור ל-Grow אומת בהצלחה." });
  };

  const save = async () => {
    if (!canSave) return;
    setSaveError("");
    const res = await saveGrowCredentials({
      clinicId,
      userId,
      pageCode,
      accountPageCode: accountPageCode.trim() || undefined,
      environment,
      source: "manual",
      settings: serializeGrowSettings(settings),
    });
    if (res.error) {
      console.error("[CK-3861] Grow credentials save failed", {
        errorCode: "CK-3861",
        errorMessage: res.error.message,
        timestamp: new Date().toISOString(),
      });
      setSaveError(res.error.message);
      return;
    }
    const growIntegration = res.data?.growIntegration || null;
    dispatch(setClinicProfile({ ...clinicProfile, isGrowIntegrated: true, growIntegration }));
    setDone(growIntegration);
    if (onSuccess) onSuccess(growIntegration);
  };

  if (!isOpen) return null;

  return (
    <div className="grow-overlay" onClick={onClose} role="presentation">
      <div className="grow-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="grow-wiz-title">
        <div className="grow-modal-header">
          <div className="grow-modal-title">
            <span className="grow-modal-icon"><Plugs size={20} weight="fill" /></span>
            <div>
              <span className="grow-eyebrow">אינטגרציה</span>
              <h3 id="grow-wiz-title">חיבור חשבון Grow</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {done ? (
          <div className="grow-modal-body grow-done">
            <span className="grow-done-icon"><CheckCircle size={44} weight="fill" /></span>
            <h4>Grow מחובר לקליניקה</h4>
            <p>מעכשיו אפשר לשלוח דרישות תשלום למטופלים מתוך עמוד הפיננסים, מהפגישה ומכרטיס המטופל.</p>
            {done.environment === "sandbox" && (
              <p className="grow-note">החיבור בסביבת בדיקות (Sandbox) — תשלומים בכרטיסי בדיקה בלבד.</p>
            )}
            <div className="grow-done-actions">
              <Link className="grow-btn primary" to="/dashboard/finance" onClick={onClose}>שליחת דרישת תשלום ראשונה</Link>
              <button type="button" className="grow-btn secondary" onClick={onClose}>סגירה</button>
            </div>
          </div>
        ) : (
          <>
            <div className="grow-tabs" role="tablist">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`grow-tab ${active ? "active" : ""}`}
                    onClick={() => setTab(t.id)}
                  >
                    <Icon size={15} weight={active ? "fill" : "regular"} />
                    <span>{t.label}</span>
                    {t.id === "credentials" && probe.state === "ok" && <CheckCircle size={14} weight="fill" className="grow-tab-ok" />}
                  </button>
                );
              })}
            </div>

            <div className="grow-modal-body">
              {tab === "intro" && (
                <div className="grow-intro">
                  <ol className="grow-steps">
                    <li>
                      <strong>פותחים את הממשק העסקי של Grow</strong>
                      <span>בתפריט הגדרות → פיתוח / API מופיעים <b>מזהה העסק (userId)</b> ו<b>קוד העמוד (pageCode)</b> של עמוד "דרישת תשלום".</span>
                    </li>
                    <li>
                      <strong>מוודאים שה-API פתוח לעסק</strong>
                      <span>אם הוא כבוי, Grow יחזירו שגיאה 300. פנו לתמיכת Grow ובקשו להפעיל גישת API לעסק.</span>
                    </li>
                    <li>
                      <strong>מדביקים כאן ובודקים חיבור</strong>
                      <span>CliniKit יוצרת קישור בדיקה לא-פעיל על סכום 1 ₪ ומבטלת אותו מיד. אין חיוב.</span>
                    </li>
                  </ol>
                  <div className="grow-callout">
                    <ShieldCheck size={18} weight="fill" />
                    <span>המזהים נשמרים בצד השרת בלבד ואינם נחשפים למטפלים או למטופלים.</span>
                  </div>
                  <a className="grow-link-external" href={GROW_BACKOFFICE_URL} target="_blank" rel="noreferrer">
                    <ArrowSquareOut size={14} /> כניסה לממשק Grow
                  </a>
                </div>
              )}

              {tab === "credentials" && (
                <div className="grow-credentials">
                  <label className="grow-field">
                    <span>מזהה העסק (userId)</span>
                    <input
                      type="text"
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      value={userId}
                      onChange={onIdsChange(setUserId)}
                      placeholder="לדוגמה: b5d587c21d74"
                    />
                  </label>
                  <label className="grow-field">
                    <span>קוד עמוד דרישת תשלום (pageCode)</span>
                    <input
                      type="text"
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      value={pageCode}
                      onChange={onIdsChange(setPageCode)}
                      placeholder="לדוגמה: 7a2c9e..."
                    />
                    <small>קוד של עמוד מסוג "דרישת תשלום / קישור לתשלום", לא של עמוד הארנק.</small>
                  </label>
                  <label className="grow-field">
                    <span>קוד עמוד חשבון לסנכרון מיתוג (אופציונלי)</span>
                    <input
                      type="text"
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      value={accountPageCode}
                      onChange={(e) => setAccountPageCode(e.target.value.trim())}
                      placeholder="ניתן להשאיר ריק"
                    />
                    <small>משמש רק ל"סנכרון מיתוג ל-Grow" (לוגו וצבעים). Grow דורשים לכך מזהה נפרד מקוד דרישת התשלום — אם אין לכם אותו, דלגו.</small>
                    {!accountCodeValid && <small className="grow-field-error">אותיות וספרות בלבד, 4–64 תווים.</small>}
                  </label>

                  <div className="grow-probe-row">
                    <button type="button" className="grow-btn secondary" onClick={runProbe} disabled={!idsValid || probe.state === "checking"}>
                      {probe.state === "checking" ? <LoaderLoop size="xs" /> : <Plugs size={16} />}
                      בדיקת חיבור
                    </button>
                    {probe.state === "ok" && (
                      <span className="grow-status ok"><CheckCircle size={14} weight="fill" />{probe.message}</span>
                    )}
                    {probe.state === "error" && (
                      <span className="grow-status error"><WarningCircle size={14} weight="fill" />{probe.message}</span>
                    )}
                  </div>
                  {environment === "sandbox" && (
                    <p className="grow-note">סביבת בדיקות (Sandbox): המזהים חייבים להיות מחשבון הבדיקות של Grow.</p>
                  )}
                </div>
              )}

              {tab === "settings" && (
                <GrowSettingsForm value={settings} onChange={setSettings} disabled={isLoading} />
              )}
            </div>

            <div className="grow-modal-footer">
              {saveError ? (
                <span className="grow-status error"><WarningCircle size={14} weight="fill" />{saveError}</span>
              ) : probe.state !== "ok" ? (
                <span className="grow-status muted"><Info size={14} weight="fill" />יש להזין את המזהים ולבצע "בדיקת חיבור" לפני השמירה.</span>
              ) : null}
              <div className="grow-footer-actions">
                <button type="button" className="grow-btn secondary" onClick={onClose} disabled={isLoading}>ביטול</button>
                <button type="button" className="grow-btn primary" onClick={save} disabled={!canSave}>
                  {isLoading && probe.state === "ok" ? <LoaderLoop size="xs" /> : <CheckCircle size={16} weight="bold" />}
                  חיבור ושמירה
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
